import React, { useState } from 'react';
import { X, CreditCard, User, Mail, MessageCircle, Loader2, Shield, Clock, Play, Users, CheckCircle } from 'lucide-react';
import { usePromo } from '../../context/PromoContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Package descriptions for checkout
const packageDescriptions = {
  booking: {
    title: 'Бронь места в Поток',
    subtitle: 'Резервирование места',
    color: 'blue',
    benefits: [
      'Гарантированное место в ближайшем потоке',
      'Доплата остатка до старта потока',
      'Возможность выбрать удобный поток',
    ],
    note: 'После оплаты мы свяжемся с вами для подтверждения и выбора даты потока.',
  },
  flow: {
    title: 'Поток',
    subtitle: 'Полный доступ',
    color: 'blue',
    benefits: [
      'Мгновенный доступ к 75 видео-урокам',
      'Участие в 9 групповых созвонах',
      '2 личных созвона с Вадимом',
      'Поддержка в чате 24/7',
    ],
    note: 'После оплаты вы сразу получите доступ к видео-урокам и начальной поддержке.',
  },
  base: {
    title: 'Обучаюсь сам',
    subtitle: 'Самостоятельное обучение',
    color: 'zinc',
    benefits: [
      'Доступ ко всем видео-урокам навсегда',
      'Авторские софты для анализа товаров',
      'Доступ в групповой чат учеников',
      'Записи созвонов с прошлых потоков',
    ],
    note: 'После оплаты вы сразу получите доступ к личному кабинету с материалами.',
  },
  advanced: {
    title: 'Продвинутый',
    subtitle: 'Личное менторство',
    color: 'violet',
    benefits: [
      'Всё из тарифа "Поток"',
      '4 личных созвона с Вадимом',
      '3 готовых поставщика для старта',
      'Приоритетная поддержка',
    ],
    note: 'После оплаты мы свяжемся с вами для планирования созвонов.',
  },
  pro: {
    title: 'Про уровень',
    subtitle: 'VIP сопровождение',
    color: 'emerald',
    benefits: [
      'Всё из тарифа "Продвинутый"',
      '10 личных созвонов с Вадимом',
      '10 готовых поставщиков',
      'Полное сопровождение до первых продаж',
    ],
    note: 'После оплаты мы свяжемся для составления персонального плана.',
  },
  ungating: {
    title: 'Ангейтинг',
    subtitle: 'Открытие закрытых брендов',
    color: 'amber',
    benefits: [
      '3 видео-урока по ангейтингу',
      '1 личный созвон с Вадимом',
      'Критерии инвойса, которые "пробивают"',
      'Секреты открытия сложных брендов',
    ],
    note: 'После оплаты вы получите доступ к материалам и договоримся о созвоне.',
  },
  'critical-mistakes': {
    title: 'Критические ошибки',
    subtitle: 'Мини-курс по ошибкам на Amazon',
    color: 'rose',
    benefits: [
      '30 минут видео-контента',
      'Чек-лист критических ошибок',
      'Доступ навсегда',
      'Опыт, который сэкономит тысячи долларов',
    ],
    note: 'После оплаты вы сразу получите доступ к мини-курсу.',
  },
  'supplier-invoice': {
    title: 'Поставщик и инвойс',
    subtitle: 'Критерии проверок Amazon',
    color: 'blue',
    benefits: [
      '2 видео-урока о логике проверок',
      'Критерии Amazon к поставщику',
      'Критерии Amazon к инвойсу',
      'Понимание решений APPROVED/REJECTED',
    ],
    note: 'После оплаты вы сразу получите доступ к видео-урокам.',
  },
  'sourcing-pack': {
    title: 'Sourcing Pack',
    subtitle: 'Системный поиск товаров',
    color: 'emerald',
    benefits: [
      '22 видео-урока по сорсингу',
      '4 практических задания',
      '3 личных созвона с Вадимом',
      'Keepa, арбитраж, поставщики',
    ],
    note: 'После оплаты вы получите доступ к материалам и договоримся о созвонах.',
  },
  'asin-checker': {
    title: 'ASIN Checker',
    subtitle: 'Софт для проверки товаров',
    color: 'cyan',
    benefits: [
      'Массовая проверка открытых/закрытых товаров',
      'Excel-отчёты с результатами',
      'Доступ к софту навсегда',
      'Экономия часов ручной проверки',
    ],
    note: 'После оплаты вы получите доступ к софту и инструкцию по использованию.',
  },
  'repricer': {
    title: 'Репрайсер',
    subtitle: 'Настройка под прибыль',
    color: 'violet',
    benefits: [
      '1 видео-урок по настройке репрайсера',
      '1 личный созвон с Вадимом',
      'Настройка под вашу стратегию',
      'Максимизация прибыли, а не "авто-режим"',
    ],
    note: 'После оплаты вы получите доступ к видео и договоримся о созвоне.',
  },
};

const CheckoutModal = ({ isOpen, onClose, packageId, packageName, price, discountMode = false }) => {
  const { getCheckoutPromoPayload } = usePromo();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    contact: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const packageInfo = packageDescriptions[packageId] || packageDescriptions.base;
  const colorStyles = {
    blue: { bg: 'from-blue-500/20 to-blue-900/10', border: 'border-blue-500/30', text: 'text-blue-400', button: 'from-blue-500 to-indigo-500' },
    zinc: { bg: 'from-zinc-500/20 to-zinc-900/10', border: 'border-zinc-500/30', text: 'text-zinc-300', button: 'from-zinc-600 to-zinc-700' },
    violet: { bg: 'from-violet-500/20 to-violet-900/10', border: 'border-violet-500/30', text: 'text-violet-400', button: 'from-violet-500 to-purple-500' },
    emerald: { bg: 'from-emerald-500/20 to-emerald-900/10', border: 'border-emerald-500/30', text: 'text-emerald-400', button: 'from-emerald-500 to-teal-500' },
    amber: { bg: 'from-amber-500/20 to-amber-900/10', border: 'border-amber-500/30', text: 'text-amber-400', button: 'from-amber-500 to-orange-500' },
    cyan: { bg: 'from-cyan-500/20 to-cyan-900/10', border: 'border-cyan-500/30', text: 'text-cyan-400', button: 'from-cyan-500 to-blue-500' },
    rose: { bg: 'from-rose-500/20 to-rose-900/10', border: 'border-rose-500/30', text: 'text-rose-400', button: 'from-rose-500 to-red-500' },
  };
  const colors = colorStyles[packageInfo.color] || colorStyles.blue;

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.contact) {
      setError('Пожалуйста, заполните все поля');
      return;
    }

    if (!formData.email.includes('@')) {
      setError('Введите корректный email');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/checkout/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: packageId,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          contact: formData.contact,
          origin_url: window.location.origin,
          discount_mode: discountMode,
          ...getCheckoutPromoPayload(),
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Ошибка при создании платежа. Попробуйте ещё раз.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} />
      
      <div className="relative w-full max-w-lg max-h-[95vh] overflow-y-auto">
        {/* Glow */}
        <div className={`absolute -inset-2 bg-gradient-to-r ${colors.bg} rounded-3xl blur-2xl`} />
        
        {/* Modal */}
        <div className={`relative bg-gradient-to-b from-zinc-900 to-zinc-950 rounded-2xl border ${colors.border} overflow-hidden`}>
          {/* Header */}
          <div className={`relative p-6 bg-gradient-to-b ${colors.bg} border-b ${colors.border}`}>
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colors.button} flex items-center justify-center shadow-xl`}>
                <CreditCard className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-black text-white">{packageInfo.title}</h2>
                <p className={`${colors.text} text-sm`}>{packageInfo.subtitle}</p>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="p-6 border-b border-zinc-800">
            <p className="text-zinc-400 text-sm mb-3">Что вы получите:</p>
            <div className="space-y-2">
              {packageInfo.benefits.map((benefit, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle className={`w-4 h-4 ${colors.text} mt-0.5 flex-shrink-0`} />
                  <span className="text-zinc-300 text-sm">{benefit}</span>
                </div>
              ))}
            </div>
            
            {/* Note */}
            <div className={`mt-4 p-3 rounded-xl bg-gradient-to-r ${colors.bg} border ${colors.border}`}>
              <p className="text-zinc-400 text-xs">{packageInfo.note}</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Price display */}
            <div className={`text-center p-4 rounded-xl bg-gradient-to-r ${colors.bg} border ${colors.border}`}>
              <p className="text-zinc-400 text-sm mb-1">К оплате:</p>
              <p className={`text-3xl font-black ${colors.text}`}>${price}</p>
            </div>

            {/* First Name */}
            <div>
              <label className="block text-zinc-400 text-sm mb-2 flex items-center gap-2">
                <User className="w-4 h-4" />
                Имя
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="Введите имя"
                className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 focus:border-amber-500 focus:outline-none transition-colors"
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-zinc-400 text-sm mb-2 flex items-center gap-2">
                <User className="w-4 h-4" />
                Фамилия
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Введите фамилию"
                className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 focus:border-amber-500 focus:outline-none transition-colors"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-zinc-400 text-sm mb-2 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="example@email.com"
                className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 focus:border-amber-500 focus:outline-none transition-colors"
              />
            </div>

            {/* Contact */}
            <div>
              <label className="block text-zinc-400 text-sm mb-2 flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                Telegram или WhatsApp
              </label>
              <input
                type="text"
                name="contact"
                value={formData.contact}
                onChange={handleChange}
                placeholder="@username или +7..."
                className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-600 focus:border-amber-500 focus:outline-none transition-colors"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-4 px-6 rounded-xl font-bold text-lg bg-gradient-to-r ${colors.button} text-white hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Загрузка...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  Перейти к оплате
                </>
              )}
            </button>

            {/* Security note */}
            <div className="flex items-center justify-center gap-2 text-zinc-500 text-xs">
              <Shield className="w-4 h-4" />
              <span>Безопасная оплата через Stripe</span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;
